import { useEffect, useRef, useState } from "react";
import { bestComboText } from "../components/Combo";
import { Modal } from "../components/Modal";
import { RankCard } from "../components/RankCard";
import { Stripes } from "../components/Stripes";
import { MODE_INFO, type Mode } from "../content/modes";
import { RANKS } from "../content/ranks";
import type { Frame } from "../game/sets";
import type { PracticeOutcome, TrialOutcome } from "../game/results";
import "./Result.css";

/** Delay before the gold or foil card reveal, after the result has sunk in. */
const SET_REVEAL_MS = 1400;

interface Props {
  /** Battles have their own screen (BattleResult). */
  outcome: TrialOutcome | PracticeOutcome;
  /** Rank currently held in the outcome's mode (for the practice card). */
  rank: number;
  /** Stripes on the card now, towards the next rank. */
  stripes: number;
  frame: Frame;
  /** The strands holding the card's rank, towards gold; none when gold isn't possible. */
  dots?: { mode: Mode; held: boolean }[] | undefined;
  onTryAgain: () => void;
  onPractice: () => void;
  onHome: () => void;
  onOpenCard: (rankIndex: number) => void;
}

/** "40 right, 3 wrong (1 passed)." plus a cheer when mistakes didn't stop them. */
export function scoreLine(
  o: { score: number; wrong: number; passes?: number },
  didWell: boolean,
): string {
  const passed = o.passes ? ` (${o.passes} passed)` : "";
  const base = `${o.score} right, ${o.wrong} wrong${passed}.`;
  return o.wrong > 0 && didWell ? `${base} Mistakes didn't stop you!` : base;
}

/** The news for the gold or foil reveal, or null when no set completed. */
export function setNews(
  o: TrialOutcome,
): { rankIndex: number; frame: "gold" | "foil"; text: string } | null {
  const foil = o.newFoil.at(-1);
  const gold = o.newGold.at(-1);
  const rankIndex = foil ?? gold;
  if (rankIndex === undefined) return null;
  const name = RANKS[rankIndex]!.name;
  const text =
    foil !== undefined
      ? `${name} is a foil card: you've earned it in all five strands!`
      : `${name} is a gold card: you've earned it in every strand you play!`;
  const up =
    o.overall > o.overallBefore ? ` Your Ninja rank is now ${RANKS[o.overall]!.name}.` : "";
  return { rankIndex, frame: foil !== undefined ? "foil" : "gold", text: text + up };
}

export function Result({
  outcome,
  rank,
  stripes,
  frame,
  dots,
  onTryAgain,
  onPractice,
  onHome,
  onOpenCard,
}: Props) {
  const news = outcome.kind === "trial" ? setNews(outcome) : null;
  const [showSet, setShowSet] = useState(false);
  const setBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!news) return;
    const t = setTimeout(() => setShowSet(true), SET_REVEAL_MS);
    return () => clearTimeout(t);
  }, [news?.rankIndex, news?.frame]); // eslint-disable-line react-hooks/exhaustive-deps

  if (outcome.kind === "practice") {
    const pct = outcome.tried ? Math.round((outcome.score / outcome.tried) * 100) : 0;
    return (
      <section className="screen result">
        <div className="result-body">
          <div className="result-text">
            <p className="result-score display">{outcome.score}</p>
            <p className="result-sub">
              {outcome.score} correct out of {outcome.tried} tried ({pct}%)
              {outcome.passes ? `, ${outcome.passes} passed` : ""}
            </p>
            {bestComboText(outcome.bestCombo) && (
              <p className="result-combo">{bestComboText(outcome.bestCombo)}</p>
            )}
            <p className="banner display">Practice done!</p>
          </div>
          <RankCard
            rankIndex={rank}
            variant={outcome.mode}
            stripes={stripes}
            onOpen={onOpenCard}
            className="result-card"
          />
          <div className="result-actions">
            <p className="next">
              Every sum makes your {MODE_INFO[outcome.mode].label.toLowerCase()} stronger.
            </p>
            <button type="button" className="big display" onClick={onPractice}>
              Practise again
            </button>
            <div className="row">
              <button type="button" className="ghost" onClick={onHome}>
                Back to the dojo
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const newStripes = outcome.stripes - outcome.stripesBefore;
  return (
    <section className="screen result">
      <div className="result-body">
        <div className="result-text">
          <p className="result-score display">{outcome.score}</p>
          <p className="result-sub">{scoreLine(outcome, outcome.passed || newStripes > 0)}</p>
          {bestComboText(outcome.bestCombo) && (
            <p className="result-combo">{bestComboText(outcome.bestCombo)}</p>
          )}
          <p className={"banner display" + (outcome.passed || newStripes > 0 ? "" : " soft")}>
            {outcome.banner}
          </p>
          {!outcome.passed && newStripes > 0 && (
            <p className="stripe-earned pop">
              <Stripes count={outcome.stripes} />
            </p>
          )}
          <p className="result-detail">{outcome.detail}</p>
        </div>
        <RankCard
          rankIndex={outcome.rank}
          variant={outcome.mode}
          stripes={stripes}
          frame={frame}
          dots={dots}
          onOpen={onOpenCard}
          className={"result-card" + (outcome.rankedUp ? " reveal" : "")}
        />
        <div className="result-actions">
          <p className="next">{outcome.nextText}</p>
          {outcome.passed ? (
            <button type="button" className="big display" onClick={onHome}>
              Back to the dojo
            </button>
          ) : (
            <>
              <button type="button" className="big display" onClick={onTryAgain}>
                Try again
              </button>
              <div className="row">
                <button type="button" className="ghost" onClick={onPractice}>
                  Practise this first
                </button>
                <button type="button" className="ghost" onClick={onHome}>
                  Back to the dojo
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {news && (
        <Modal
          open={showSet}
          label={news.frame === "foil" ? "Foil card" : "Gold card"}
          onClose={() => setShowSet(false)}
          initialFocus={setBtn}
        >
          <div className="panel welcome set-reveal">
            <h2 className="display">{news.frame === "foil" ? "Foil card!" : "Gold card!"}</h2>
            <RankCard
              rankIndex={news.rankIndex}
              variant={outcome.mode}
              frame={news.frame}
              className="set-card reveal"
            />
            <p>{news.text}</p>
            <button
              ref={setBtn}
              type="button"
              className="big display"
              onClick={() => setShowSet(false)}
            >
              Awesome!
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
